import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { apiFetch } from "@/utils/api";

interface TxType {
  id: string;
  name: string;
  direction: string;
}

export function AdminTransactionTypesPage() {
  const [types, setTypes] = useState<TxType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", direction: "in" });
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const data = await apiFetch<{ transactionTypes: TxType[] }>("/transaction-types");
    setTypes(data.transactionTypes);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const create = async () => {
    await apiFetch("/transaction-types", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setShowNew(false);
    setForm({ name: "", direction: "in" });
    await load();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Transaction Types"
        actions={
          <Button onClick={() => setShowNew(!showNew)} className="bg-[#1e3a4f] hover:bg-[#163041]">
            {showNew ? "Cancel" : "Add Type"}
          </Button>
        }
      />
      {showNew && (
        <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-end max-w-lg">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Direction</Label>
            <Select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
              <option value="in">In</option>
              <option value="out">Out</option>
              <option value="adjust">Adjust</option>
            </Select>
          </div>
          <Button onClick={create} className="bg-[#1e3a4f] hover:bg-[#163041]">Create</Button>
        </div>
      )}
      <DataTable
        columns={[
          { key: "name", header: "Name", render: (t) => t.name },
          { key: "dir", header: "Direction", render: (t) => t.direction },
        ]}
        rows={types}
        rowKey={(t) => t.id}
      />
    </div>
  );
}
